import { useCallback } from "react";
import { ChatType, ParticipantType } from "../../../types/chatTypes";
import { extractFromUniqueName } from "../../../utils/chatUtils";

export interface ChatHandlerDependencies {
  setOpenSupplierChatData?: (data: any) => void;
  setOpenSupplierChatUniqueName?: (uniqueName: string) => void;
  setOpenCustomerChatData?: (data: any) => void;
  setOpenCustomerChatUniqueName?: (uniqueName: string) => void;
  userDetails: { _id: string };
}

export const createUnifiedChatHandler = (deps: ChatHandlerDependencies) => {
  const {
    setOpenSupplierChatData,
    setOpenSupplierChatUniqueName,
    setOpenCustomerChatData,
    setOpenCustomerChatUniqueName,
    userDetails,
  } = deps;

  const onChatClick = async (uniqueName: string, conversation?: any) => {
    const { orderNo, supplierId, customerId } =
      extractFromUniqueName(uniqueName);

    const chatType = uniqueName.includes(`${ChatType.SUPPLIER}:`)
      ? ChatType.SUPPLIER
      : ChatType.CUSTOMER;

    const participantName = await getParticipantName({
      chatType,
      conversation,
    });

    if (chatType === ChatType.SUPPLIER) {
      setOpenSupplierChatData?.({
        chatType: ChatType.SUPPLIER,
        orderNumber: orderNo,
        participantName,
        orderHandlerUserId: userDetails._id,
        participantId: supplierId,
        uniqueName,
      });
      setOpenSupplierChatUniqueName?.(uniqueName);
    } else {
      setOpenCustomerChatData?.({
        chatType: ChatType.CUSTOMER,
        orderNumber: orderNo,
        participantName,
        orderHandlerUserId: userDetails._id,
        participantId: customerId,
        uniqueName,
      });
      setOpenCustomerChatUniqueName?.(uniqueName);
    }
  };

  const getParticipantName = async ({
    chatType,
    conversation,
  }: {
    chatType: ChatType;
    conversation: any;
  }) => {
    try {
      if (!conversation)
        return chatType === ChatType.SUPPLIER ? "Supplier" : "Customer";
      const participants = await conversation.getParticipants();
      const participantsData: any = {};
      participants.forEach((participant: any) => {
        const attributes = participant.attributes as any;
        if (attributes?.role && attributes?.name) {
          participantsData[attributes.role] = attributes.name;
        }
      });

      if (chatType === ChatType.SUPPLIER) {
        return participantsData[ParticipantType.SUPPLIER];
      } else if (chatType === ChatType.CUSTOMER) {
        return participantsData[ParticipantType.CUSTOMER];
      }

      return "Unknown";
    } catch (error) {
      console.warn("Failed to get participants:", error);
      return "Unknown";
    }
  };

  const getParticipantId = ({ chatType, supplierId, customerId }: any) => {
    return chatType === ChatType.SUPPLIER ? supplierId : customerId;
  };

  return {
    onChatClick,
    getParticipantName,
    getParticipantId,
  };
};
